import { useForm, Controller } from 'react-hook-form'
import { gql } from 'graphql-request'
import { LockClosedIcon } from '@heroicons/react/24/outline'

import { useMutation } from '@hooks/graphql'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Form } from '@/components/ui/form'
import Collapsible from '@/components/Collapsible'
import { useFlashMessage } from '@components/FlashMessage'

import CoverImage from './CoverImage'

const UDPATE_SERIES_MUTATION = gql`
  mutation updateSeries($input: UpdateSeriesInput!) {
    updateSeries(input: $input) {
      series {
        id
        name
        description
        notes
        createdAt
        visibility
      }
    }
  }
`

const Settings = ({ id, refetch, name, description, visibility, imageUrl, notes }) => {
  const { setFlashMessage } = useFlashMessage()
  const form = useForm({
    mode: 'onTouched',
    defaultValues: {
      name,
      description,
      notes,
      visibility
    }
  })

  const { mutate: updateSeries, isLoading } = useMutation({
    gqlMutation: UDPATE_SERIES_MUTATION,
    onSuccess: () => {
      setFlashMessage(`${form.getValues('name') || name} saved!`, 'success')
      refetch()
    },
    onError: () => {
      setFlashMessage(`Failed to save ${form.getValues('name') || name}`, 'error')
    }
  })

  const onSubmit = data => {
    console.log(data)
    updateSeries({ input: { seriesId: id, ...data } })
  }

  return (
    <div className='w-full px-4 py-8'>
      <div className='flex flex-col max-w-3xl mx-auto gap-5'>
        <CoverImage
          className='w-full'
          seriesId={id}
          refetch={refetch}
          imageUrl={imageUrl}
        />

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex flex-col gap-4'
          >
            <div className='space-y-2'>
              <Label htmlFor='name'>Name</Label>
              <Input
                id='name'
                {...form.register('name', { required: 'Name is required' })}
              />
              <If condition={form.formState.errors.name}>
                <p className='text-sm text-red-500 font-semibold' role='alert'>{form.formState.errors.name.message}</p>
              </If>
            </div>

            <div className='space-y-2'>
              <Label htmlFor='description'>Description</Label>
              <Controller
                name='description'
                control={form.control}
                render={({ field }) => (
                  <Textarea
                    id='description'
                    {...field}
                  />
                )}
              />
            </div>

            <div className='space-y-2 mb-1'>
              <Label htmlFor='visibility'>Visibility</Label>
              <Controller
                name='visibility'
                control={form.control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger id='visibility' className='w-[350px] !h-auto'>
                      <SelectValue placeholder='Select visibility' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='PRIVATE'>
                        <div className='flex items-start py-1'>
                          <LockClosedIcon className='h-5 w-5 mr-3 shrink-0' />
                          <div>
                            <p className='select-item-title leading-none font-medium text-left mb-1'>Private</p>
                            <small className='select-item-description leading-none text-muted-foreground text-left'>Only available to you</small>
                          </div>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>


            <Button
              className='ml-auto w-fit'
              disabled={isLoading}
              type='submit'
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}

export default Settings
